import { Grid, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import { MAIN_THEME_COLOR } from '../../assets/theme/Colors';
import {
	CNDataIcon,
	DownloadSmallIcon,
	FileIcon,
	ICPDataIcon,
	OCPDataIcon,
	UserCheck,
	UserDataIcon,
	ViewELIcon,
} from '../../assets/icons/Icons';
import { dateConverter } from '../../Utils/Helpers';

export function ActionButton({ BtnIcon, onClick, disabled }) {
	return (
		<IconButton
			disabled={disabled}
			sx={{
				marginRight: '3px',
			}}
			onClick={onClick}>
			<BtnIcon />
		</IconButton>
	);
}

export function MetricTile({ count, name, TileIcon, description }) {
	return (
		<Grid
			item
			xs={12}
			md={3}
			sx={{
				backgroundColor: '#FCFDFF',
				borderRadius: '6px',
				flexDirection: 'row',
				alignItems: 'center',
				display: 'flex',
				paddingX: '24px',
				paddingY: '12px',
				border: '0.6px solid #F1F3FA',
			}}>
			<TileIcon />
			<Grid
				container
				sx={{ flexDirection: 'column', marginLeft: '15px' }}>
				<Typography
					color={'#5E5C5C'}
					fontSize={'13px'}
					paddingBottom={'5px'}
					fontWeight={'400'}>
					{name}
				</Typography>
				<Typography
					color={'#324054'}
					fontSize={'18px'}
					paddingY={'2.5px'}
					fontWeight={'600'}>
					{count}
				</Typography>
				<Typography
					color={'#6B7280'}
					fontSize={'12px'}
					paddingTop={'5px'}
					fontWeight={'500'}>
					{description}
				</Typography>
			</Grid>
		</Grid>
	);
}

export function FilterTile({ name, onClick, isSelected }) {
	return (
		<IconButton
			disableRipple
			sx={{
				color: isSelected ? '#FFFFFF' : '#242731',
				fontWeight: '500',
				padding: '4px 10px 4px 10px',
				borderRadius: '22px',
				fontSize: '14px',
				marginX: '5px',
				backgroundColor: isSelected ? MAIN_THEME_COLOR : '#FFFFFF',
			}}
			onClick={onClick}>
			{name}
		</IconButton>
	);
}

function TextWithTooltip({ text }) {
	if (text.length < 16) {
		return (
			<Typography fontSize={'10px'} color={'#28696D'} marginLeft={'2px'}>
				{text}
			</Typography>
		);
	} else {
		return (
			<Tooltip title={text} placement="right" arrow>
				<Typography
					fontSize={'10px'}
					color={'#28696D'}
					marginLeft={'2px'}
					noWrap>
					{text.slice(0, 16) + '...'}
				</Typography>
			</Tooltip>
		);
	}
}

export function DataTile({
	elderId,
	elderName,
	admin,
	type,
	content,
	addedDate,
	filters,
	onViewPlan,
}) {
	const getIcon = () => {
		if (type === filters[1]) {
			return <CNDataIcon />;
		} else if (type === filters[2]) {
			return <ICPDataIcon />;
		} else if (type === filters[3]) {
			return <OCPDataIcon />;
		}
	};

	const getTitle = () => {
		if (type === filters[1]) {
			return filters[1];
		} else if (type === filters[2]) {
			return filters[2];
		} else if (type === filters[3]) {
			return filters[3];
		}
	};

	return (
		<Grid
			sx={{
				width: '100%',
				padding: '5px',
				backgroundColor: '#FCFDFF',
				borderRadius: '6px',
				border: '0.7px #E5E7EB solid',
			}}>
			<Grid
				container
				width={'100%'}
				padding={'10px'}
				textAlign={'left'}
				flexDirection={'column'}
				borderRadius={'5px'}>
				<Grid
					container
					flexDirection={'row'}
					paddingBottom={'5px'}
					justifyContent={'space-between'}
					alignItems={'center'}>
					<Grid
						width={'70%'}
						flexDirection={'row'}
						display={'flex'}
						alignItems={'center'}>
						{getIcon()}
						<Typography
							fontWeight={'700'}
							fontSize={'14px'}
							margin={'5px'}
							color={'#787486'}>
							{getTitle()}
						</Typography>
						<Typography
							fontWeight={'700'}
							fontSize={'12px'}
							marginLeft={'5px'}
							color={'#AABDCE'}>
							{dateConverter(addedDate)}
						</Typography>
					</Grid>
					{elderId ? (
						<Grid
							bgcolor={'#87B6B31F'}
							padding={'5px 10px 5px 10px'}
							display={'flex'}
							flexDirection={'row'}
							borderRadius={'10px'}>
							<UserDataIcon />
							<TextWithTooltip
								text={`${elderId}: ${elderName}`}
							/>
						</Grid>
					) : (
						<Skeleton width={'40%'} />
					)}
				</Grid>
				{Array.isArray(content) ? (
					<IconButton
						disableFocusRipple
						disableRipple
						onClick={onViewPlan}>
						<Typography
							sx={{
								whiteSpace: 'normal',
								wordBreak: 'break-word',
								padding: '10px',
								fontSize: '14px',
								color: '#3D4A58',
							}}
							fontWeight={'500'}>
							Click to view plan
						</Typography>
					</IconButton>
				) : (
					<Typography
						sx={{
							whiteSpace: 'normal',
							wordBreak: 'break-word',
							padding: '10px',
							fontSize: '14px',
							color: '#3D4A58',
						}}>
						{content}
					</Typography>
				)}
			</Grid>
			<Grid container justifyContent={'flex-end'}>
				<UserCheck />
				<Typography
					paddingRight={'10px'}
					paddingLeft={'5px'}
					fontSize={'10px'}
					borderRadius={'10px'}
					color="#3D4A58">
					{`Updated By: ${admin}`}
				</Typography>
			</Grid>
		</Grid>
	);
}

export function UserAnalyticsSection({ name, percentage, color }) {
	return (
		<Grid
			container
			sx={{
				width: '130px',
				height: '35px',
				borderRadius: '8px',
			}}>
			<Grid
				sx={{
					backgroundColor: color,
					height: '22px',
					width: '9px',
					borderRadius: '5px',
					marginLeft: '5px',
				}}
			/>
			<Grid marginLeft={'10px'}>
				<Typography
					fontSize={'12px'}
					fontWeight={'400'}
					color={'#5E5C5C'}>
					{name}
				</Typography>
				<Typography
					fontSize={'14px'}
					color={'#324054'}
					fontWeight={'600'}>
					{percentage}
				</Typography>
			</Grid>
		</Grid>
	);
}

export function ItemTile({ color, name }) {
	return (
		<Grid container flexDirection={'row'} alignItems={'center'}>
			<Grid
				sx={{
					backgroundColor: color,
					height: '14px',
					width: '14px',
					borderRadius: '5px',
					marginLeft: '5px',
				}}
			/>
			<Typography
				marginLeft={'5px'}
				fontSize={'12px'}
				fontWeight={'400'}
				color={'#5E5C5C'}>
				{name}
			</Typography>
		</Grid>
	);
}

export function FileTile({ name, addedDate, onView, onDownload }) {
	return (
		<Grid
			sx={{
				width: '100%',
				padding: '15px 20px 15px 20px',
				backgroundColor: '#FCFDFF',
				borderRadius: '6px',
				border: '0.7px #E5E7EB solid',
				justifyContent: 'space-between',
				flexDirection: 'row',
				display: 'flex',
				alignItems: 'center',
			}}>
			<Grid item display={'flex'} alignItems={'center'}>
				<FileIcon />
				<Grid
					display={'flex'}
					flexDirection={'column'}
					marginLeft={'15px'}>
					<Typography fontSize={'14px'} color={'#5E5C5C'}>
						{name}
					</Typography>
					<Typography
						fontSize={'11px'}
						color={'#CECECE'}
						marginTop={'5px'}>
						{addedDate}
					</Typography>
				</Grid>
			</Grid>
			<Grid item>
				<ActionButton BtnIcon={ViewELIcon} onClick={onView} />
				<ActionButton
					BtnIcon={DownloadSmallIcon}
					onClick={onDownload}
				/>
			</Grid>
		</Grid>
	);
}
